import { useEffect, useRef, useState } from "react";
import { Props_SnippetStats } from "./Stats.props";
import { CompileMarkdown } from '../../../../Shared/AttraxTs/React/ReactSnippetMDCompiler'

const SnippetStats = (props: Props_SnippetStats) => {
    const { heading, repeatable_stat, properties } = props;
    const headiningClass = heading?.visibility?.value === "hidden" ? "hidden" : "visible";
    const sectionRef = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(false);

    // Track animated values for each stat
    const [animatedValues, setAnimatedValues] = useState<number[]>(
        repeatable_stat.map(() => 0)
    );

    // Intersection Observer to trigger animation
    useEffect(() => {
        const section = sectionRef.current;
        if (!section) return;

        const observer = new window.IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.3 }
        );
        observer.observe(section);

        return () => observer.disconnect();
    }, []);

    // Animate numbers when visible
    useEffect(() => {
        if (!visible) return;

        repeatable_stat.forEach((stat, idx) => {
            const endValue = parseInt(stat.statValue?.value || "0", 10);
            if (isNaN(endValue)) return;


            const duration = properties.statDuration?.value || 2400; // ms
            const startTime = performance.now();

            function animate(now: number) {
                const elapsed = now - startTime;
                const progress = Math.min(elapsed / Number(duration), 1);
                const current = Math.floor(progress * endValue);

                setAnimatedValues(prev => {
                    const updated = [...prev];
                    updated[idx] = current;
                    return updated;
                });

                if (progress < 1) {
                    requestAnimationFrame(animate);
                } else {
                    setAnimatedValues(prev => {
                        const updated = [...prev];
                        updated[idx] = endValue;
                        return updated;
                    });
                }
            }

            requestAnimationFrame(animate);
        });
    }, [visible]);

    // Format number with commas
    const formatNumber = (value: number) =>
        value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    return (
        <section className="snippet-stats" id={properties?.snippetid?.value} ref={sectionRef}>
            <div className="snippet-stats__container">
                <h2 className={"snippet-stats__heading snippet-stats__heading--" + headiningClass}>{heading.content?.value}</h2>
                <div className="snippet-stats__stats-container">
                    {repeatable_stat.map((stat, index) => (
                        <div key={`snippet-stats-${index}`} className={"snippet-stats__stat" + (stat.showStatImage?.value === 'Yes' ? " snippet-stats__stat--with-image" : "")}>
                            {stat.showStatImage?.value === 'Yes' && (
                                <figure className="snippet-stats__image-container">
                                    <img
                                        className="snippet-stats__stat-image"
                                        src={stat.statImage?.value}
                                        alt={stat.statText?.value ? stat.statText.value : ''}
                                        loading="lazy"
                                        height="300"
                                        width="270"
                                    /></figure>
                            )}
                            <div className="snippet-stats__content-container">
                                <h3 className="snippet-stats__stat-value" data-count={stat.statValue?.value}>
                                    {stat.statPrepend?.value && <span className="snippet-stats__prepend">{stat.statPrepend.value}</span>}
                                    {formatNumber(animatedValues[index] || 0)}
                                    {stat.statAppend?.value && <span className="snippet-stats__append">{stat.statAppend.value}</span>}
                                </h3>
                                {stat.statText?.value && <div className="snippet-stats__stat-text">{CompileMarkdown(stat.statText.value)}</div>}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    )
}

export default SnippetStats;